import { useCallback, useState } from "react";
import { defineMessages } from "react-intl";
import Slider from "rc-slider";
import classNames from "classnames";

import { useUniqueId } from "../Utils/uniqueId";
import { useFormatMessage } from "../Intl/Context";

const RED_COLOR = "#dc3545";
const GREEN_COLOR = "#28a745";
const NEUTRAL_COLOR = "#6c757d";

const GaugeSlider = props => {
  const {
    budgetLine,
    currentOptionId,
    onValueChange,
    disabled = false,
    className
  } = props;
  const { budgetLineOptions = [] } = budgetLine;

  const sliderDomId = useUniqueId("gauge-slider-");

  const t = useFormatMessage();
  const selectedValueLabel = t(intlMessages.selectedValueLabel);

  // Find the current option index based on the option ID or value
  const findOptionIndex = useCallback(
    (optionId, value) => {
      if (optionId) {
        const option = budgetLineOptions.find(option => option.id === optionId);
        if (option) return option.position;
      }
      const option = budgetLineOptions.find(option => option.value === value);
      return option ? option.position : 0;
    },
    [budgetLineOptions]
  );

  const [currentOptionIndex, setCurrentOptionIndex] = useState(() =>
    findOptionIndex(currentOptionId, budgetLine.value)
  );

  const handleSliderChange = useCallback(
    optionIndex => {
      setCurrentOptionIndex(optionIndex);

      const selectedOption = budgetLineOptions[optionIndex];

      if (selectedOption && onValueChange) {
        onValueChange(selectedOption);
      }
    },
    [budgetLineOptions, onValueChange]
  );

  if (budgetLineOptions.length === 0) {
    return null;
  }

  const currentOption = budgetLineOptions[currentOptionIndex] || budgetLineOptions[0];
  const sliderClassName = classNames("gauge-slider", className);

  // Prepare marks for the slider
  const marks = budgetLineOptions.reduce((acc, option, index) => {
    acc[index] = {
      style: {
        fontSize: "12px",
        marginTop: "8px"
      },
      label: option.label
    };
    return acc;
  }, {});

  return (
    <div className={sliderClassName}>
      <output
        htmlFor={sliderDomId}
        className="d-block text-right text-small margin-bottom-sm gauge-current-value"
        /* Don't announce changes as they will be announced by the .rc-slider-handle 
           element, which has a "slider" `role` and an `aria-valuenow` attribute. */
        aria-live="false"
      >
        <span className="d-inline-block margin-right-xxs">{selectedValueLabel}</span>

        <b>{currentOption.label}</b>
      </output>

      <Slider
        id={sliderDomId}
        min={0}
        max={budgetLineOptions.length - 1}
        step={1}
        value={currentOptionIndex}
        onChange={handleSliderChange}
        marks={marks}
        disabled={disabled}
        trackStyle={{
          backgroundColor: currentOption.value >= 0 ? GREEN_COLOR : RED_COLOR
        }}
        handleStyle={{
          borderColor: currentOption.value >= 0 ? GREEN_COLOR : RED_COLOR
        }}
        dotStyle={{
          borderColor: NEUTRAL_COLOR
        }}
        activeDotStyle={{
          borderColor: currentOption.value >= 0 ? GREEN_COLOR : RED_COLOR
        }}
      />
    </div>
  );
};

export default GaugeSlider;

const intlMessages = defineMessages({
  selectedValueLabel: {
    id: "input.gaugeSlider.selectedValueLabel",
    defaultMessage: "Valeur sélectionnée :"
  }
});
